/* @flow */

import React, { Component } from 'react'
import { connect } from 'react-redux'
import styled from 'styled-components'
import chunk from 'lodash/chunk'
import { Link } from 'react-router-dom'
import type { RouterHistory } from 'react-router-dom'

import DropShippingModal from './DropShippingModal'
import OrderImageDownloader from '../file-bank/components/OrderImageDownloader'
import BrandListDashboard from './BrandListDashboard'

import Address from '../entities/components/Address'
import { switchEntity } from '../template/actions'
import CloudinaryResource from '../../infrastructure/components/CloudinaryResource'
import { PermissionContext } from '../../infrastructure/components/Authorization'

import { getAvailableBrands, getAvailableRetailers } from './actions'
import type { AvailableBrand, Id } from '../types'
import { canShopBrand } from './shared'

type Props = {
  dispatch: Function,
  history: RouterHistory,
}

type State = {
  brands: Array<AvailableBrand>,
  isFetching: boolean,
  retailers: Array<Object>,
  showOrderImageDownloader: Id | false,
  showDropShippingModal: AvailableBrand | false,
}

class BrandList extends Component<Props, State> {
  state = {
    brands: [],
    isFetching: false,
    retailers: [],
    showOrderImageDownloader: false,
    showDropShippingModal: false,
  }

  componentDidMount() {
    this.setState({
      isFetching: true,
    })

    this.getData()
  }

  getData = () => {
    const { dispatch, history } = this.props

    Promise.all([
      dispatch(getAvailableBrands()),
      dispatch(getAvailableRetailers()),
    ]).then(([brandResponse, retailerResponse]) => {
      const changes: $Shape<State> = { isFetching: false }

      if (!brandResponse.error) {
        const brands = brandResponse.payload.brands
        for (let brand of brands) {
          if (brand.is_visiting_custom_url === true && canShopBrand(brand)) {
            history.push(`/shop/${brand.id}`)
          }
        }

        changes.brands = brands
      }
      if (!retailerResponse.error) {
        changes.retailers = retailerResponse.payload.retailers
      }

      this.setState(changes)
    })
  }

  render() {
    const { user } = this.props
    const {
      brands,
      isFetching,
      retailers,
      showOrderImageDownloader,
      showDropShippingModal,
    } = this.state

    if (isFetching) {
      return null
    }

    const primaryRetailer = retailers.filter(b => b.type === 'primary')[0]
    const mergeable = retailers.filter(b => b.type === 'mergeable')
    const nonMergeable = retailers.filter(b => b.type === 'non_mergeable')

    return (
      <Container>
        <BrandListContainer>
          {showOrderImageDownloader && (
            <OrderImageDownloader
              onHide={() => this.setState({ showOrderImageDownloader: false })}
              show={showOrderImageDownloader}
            />
          )}
          {showDropShippingModal !== false && (
            <DropShippingModal
              brand={showDropShippingModal}
              onHide={() => this._onShowDropShippingModal(false)}
              onUpdateCustomer={this.getData}
              show={showDropShippingModal !== false}
            />
          )}

          <InnerContainer>
            <HeroContainer>
              <WelcomeHero>Welcome {user.firstname}!</WelcomeHero>
              <HeroSubtitle>
                You are logged in using e-mail <strong>{user.email}</strong>.
                <br />
                You can find all the brands you are connected to from the list
                below. If you are looking for one but cannot find it, maybe they
                invited you on a different e-mail
              </HeroSubtitle>
            </HeroContainer>

            {brands.length === 0 && (
              <div
                className="alert alert-info"
                style={{ marginTop: 20, fontSize: 16 }}
              >
                You are currently not connected to any brands on Traede. This
                may be because a brand has removed your B2B access. If you think
                this is an error, reach out to{' '}
                <a href="mailto:support@traede.com">support@traede.com</a>
              </div>
            )}
            {brands.length > 0 && (
              <GridLayout
                brands={brands}
                onGoToShowroom={this._onGoToShowroom}
                onShopBrand={this._onShopBrand}
                onShowOrderImageDownloader={brandId =>
                  this.setState({ showOrderImageDownloader: brandId })
                }
                onShowDropShippingModal={this._onShowDropShippingModal}
              />
            )}
          </InnerContainer>
        </BrandListContainer>
        <FooterContainer>
          <InnerContainer style={{ width: 790 }}>
            <FooterHeader>Your company on Traede</FooterHeader>

            {/*<FooterBox>
              <FooterBoxTitle>Want more brands in Traede?</FooterBoxTitle>

              <FooterBoxDescription>
                Do you like shopping on Traede? You can help us get more brands on Traede. If you purchase from more brands{" "}
                you can refer them to us. We will reach out and hear if they are interested to join Traede as well.
              </FooterBoxDescription>

              <button type="button" className="btn btn-success">Invite brands to start using Traede</button>
            </FooterBox>*/}

            <FooterBox>
              {primaryRetailer
                ? renderRetailer(primaryRetailer, false, true)
                : null}
            </FooterBox>

            <ContactBox>
              <FooterBoxTitle>Do you need help?</FooterBoxTitle>

              <FooterBoxRow style={{ textAlign: 'center' }}>
                <div>
                  <a href="https://intercom.help/traede" target="_blank">
                    <span className="glyphicon glyphicon-share" /> Check out{' '}
                    <strong>Traede's HelpDesk</strong>
                  </a>
                </div>

                <div>
                  <a href="mailto:support@traede.com">
                    <span className="glyphicon glyphicon-envelope" /> Email us
                    at <strong>support@traede.com</strong>
                  </a>
                </div>

                <div>
                  <span className="glyphicon glyphicon-phone" /> Call us at{' '}
                  <strong>(+45) 71 99 00 66</strong>
                </div>
              </FooterBoxRow>
            </ContactBox>
          </InnerContainer>
        </FooterContainer>
      </Container>
    )
  }

  _onGoToShowroom = (brand, showroom) => {
    this.props.history.push(
      `/showroom/${brand.id}/${showroom.id}/${showroom.hash}`
    )
  }

  _onShopBrand = (brand: AvailableBrand) => {
    this.props.history.push(`/shop/${brand.id}`)
  }

  _onShowDropShippingModal = (brand: AvailableBrand | false) => {
    this.setState({
      showDropShippingModal: brand,
    })
  }
}

const renderRetailer = (retailer: Object, bordered, last, actions) => {
  return (
    <Company bordered={bordered} last={last}>
      <CompanyTitle>Company: {retailer.name}</CompanyTitle>

      <FooterBoxRow>
        <div>
          <FooterBoxColumnTitle>Address</FooterBoxColumnTitle>

          {retailer.address && (
            <Address address={retailer.address} inline={false} />
          )}
          {!retailer.address && <span>No address entered yet.</span>}
        </div>
        <div>
          <FooterBoxColumnTitle>First invited by</FooterBoxColumnTitle>

          {retailer.brands.length > 0 && retailer.brands[0].name}
        </div>
        <div>
          <FooterBoxColumnTitle>Connected to</FooterBoxColumnTitle>

          {retailer.brands.map(brand => {
            return <div>{brand.name}</div>
          })}
        </div>
      </FooterBoxRow>

      {actions}
    </Company>
  )
}

export default connect(state => ({
  user: state.session.user,
}))(BrandList)

const Container = styled.div``

const BrandListContainer = styled.div`
  background: white;
  border-bottom: 1px solid #f3f3f3;
  display: flex;
  justify-content: center;
  margin-top: 35px;
`

const BrandListDashboardContainer = styled.div``

const FooterContainer = styled.div`
  background: #fafbfb;
  display: flex;
  justify-content: center;
  padding: 25px 0 60px 0;
`

const FooterBox = styled.div`
  background: #fff;
  border: 1px solid #f3f3f3;
  margin-bottom: 20px;
  padding: 25px 30px;
`

const FooterBoxAlert = styled.div.attrs({ className: 'alert alert-info' })``

const FooterBoxDescription = styled.p`
  margin-bottom: 15px;
`

const FooterBoxTitle = styled.div`
  font-size: 20px;
  line-height: 20px;
  margin-bottom: 15px;
`

const FooterHeader = styled.div`
  font-size: 28px;
  line-height: 28px;
  margin-bottom: 25px;
`

const ContactBox = styled.div`
  align-items: center;
  background: #2ab1df;
  color: white;
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 30px;

  a {
    color: white;
  }
`

const FooterBoxRow = styled.div`
  display: flex;
  width: 100%;

  > div {
    flex: 1;
  }
`

const FooterBoxColumnTitle = styled.div`
  color: #ccc;
  font-size: 12px;
  font-weight: bold;
  text-transform: uppercase;
`

const InnerContainer = styled.div`
  max-width: 1170px;
  width: 100%;
`

const Company = styled.div`
  border: ${({ bordered }: { bordered: boolean }) =>
    bordered ? '1px solid #e2e2e2' : 'none'};
  margin-bottom: ${({ last }: { last: boolean }) => (!last ? '25px' : 0)};
  padding: ${({ bordered }: { bordered: boolean }) =>
    bordered ? '10px' : 'none'};
`

const CompanyTitle = styled.div`
  font-size: 16px;
  font-weight: bold;
  margin-bottom: 10px;
`

const CompanyActions = styled.div`
  background: #f8f8f8;
  margin: 10px -10px -10px -10px;
  padding: 10px;
`

export const GridLayout = ({
  brands,
  onGoToShowroom,
  onShopBrand,
  onShowDropShippingModal,
  onShowOrderImageDownloader,
}: {
  brands: Array<AvailableBrand>,
  onGoToShowroom: (showroom: Object) => void,
  onShopBrand: (brand: AvailableBrand) => void,
  onShowDropShippingModal: (brand: AvailableBrand) => void,
  onShowOrderImageDownloader: boolean,
}) => {
  const brandList = React.useMemo(() => {
    return brands.filter(brand => canShopBrand(brand))
  }, [brands, canShopBrand])

  const otherBrands = React.useMemo(() => {
    return brands.filter(brand => !canShopBrand(brand))
  }, [brands, canShopBrand])

  const chunked = chunk(brandList, 3)
  const chunkedOther = chunk(otherBrands, 3)

  const [showShowrooms, setShowShowrooms] = React.useState(false)
  const hideShowrooms = React.useCallback(() => {
    if (!showShowrooms) {
      return
    }

    setShowShowrooms(false)
  }, [showShowrooms, setShowShowrooms])

  React.useEffect(() => {
    document.addEventListener('click', hideShowrooms)

    return () => {
      document.removeEventListener('click', hideShowrooms)
    }
  }, [hideShowrooms])

  return (
    <>
      <GridLayoutContainer>
        {chunked.map(brands => {
          return (
            <GridLayoutRow>
              {brands.map(brand => {
                const retailerConnection = brand.retailer_connections[0]

                let noAccessMessage =
                  brand.retailer_connections.length > 0
                    ? brand.retailer_connections[0].b2b_no_access_message
                    : ''

                if (typeof noAccessMessage !== 'string') {
                  noAccessMessage = ''
                }

                const canShop = canShopBrand(brand)
                const hasFileBank =
                  brand.retailer_connections.filter(
                    conn => conn.file_bank === true
                  ).length > 0

                return (
                  <GridLayoutCompany key={brand.id}>
                    <GridLayoutLogoContainer>
                      {brand.logo && (
                        <GridLayoutLogo
                          brandId={brand.id}
                          id={brand.logo}
                          presets="brand_list_grid_logo"
                          fallback="brand_list_grid_logo"
                          alt={brand.name}
                          title={brand.name}
                        />
                      )}

                      {!brand.logo && <span>{brand.name}</span>}
                    </GridLayoutLogoContainer>
                    <GridLayoutActions>
                      {canShop && (
                        <div>
                          <PermissionContext permission="webshop:browse">
                            <ShopButton
                              type="button"
                              onClick={() => onShopBrand(brand)}
                            >
                              Shop now
                            </ShopButton>
                          </PermissionContext>
                        </div>
                      )}
                      {brand.showrooms.length > 0 && (
                        <div style={{ position: 'relative' }}>
                          <PermissionContext permission="webshop:browse">
                            <ShopButton
                              type="button"
                              onClick={() => setShowShowrooms(brand.id)}
                            >
                              Showrooms
                            </ShopButton>

                            <DropDown
                              className={
                                showShowrooms === brand.id ? 'active' : ''
                              }
                            >
                              {brand.showrooms.map(showroom => (
                                <li>
                                  <button
                                    onClick={() =>
                                      onGoToShowroom(brand, showroom)
                                    }
                                  >
                                    {showroom.title}
                                  </button>
                                </li>
                              ))}
                            </DropDown>
                          </PermissionContext>
                        </div>
                      )}
                      {!canShop && (
                        <div>
                          <strong>
                            {noAccessMessage.length > 0
                              ? noAccessMessage
                              : 'You do not currently have access to this B2B webshop'}
                          </strong>
                        </div>
                      )}
                      <div>
                        <button
                          type="button"
                          className="btn btn-white btn-sm"
                          onClick={() => onShowOrderImageDownloader(brand.id)}
                        >
                          Download product images
                        </button>
                      </div>
                      {hasFileBank && (
                        <div>
                          <Link
                            to={`/file-bank/${brand.id}`}
                            type="button"
                            className="btn btn-white btn-sm"
                          >
                            Open File bank
                          </Link>
                        </div>
                      )}
                      {retailerConnection &&
                        retailerConnection.dropshipping && (
                          <div>
                            <button
                              type="button"
                              className="btn btn-white btn-sm"
                              onClick={() => onShowDropShippingModal(brand)}
                            >
                              <span className="label label-xxs label-warning">
                                NEW
                              </span>{' '}
                              Dropshipping
                            </button>
                          </div>
                        )}
                    </GridLayoutActions>
                  </GridLayoutCompany>
                )
              })}
            </GridLayoutRow>
          )
        })}
      </GridLayoutContainer>

      {otherBrands.length > 0 && (
        <>
          <h3
            style={{
              marginTop: 25,
              textAlign: 'center',
              fontWeight: 500,
              borderBottom: '1px solid #a8a8a8',
              paddingBottom: 20,
            }}
          >
            Other brands
          </h3>

          <GridLayoutContainer>
            {chunkedOther.map(brands => {
              return (
                <GridLayoutRow>
                  {brands.map(brand => {
                    const retailerConnection = brand.retailer_connections[0]

                    let noAccessMessage =
                      brand.retailer_connections.length > 0
                        ? brand.retailer_connections[0].b2b_no_access_message
                        : ''

                    if (typeof noAccessMessage !== 'string') {
                      noAccessMessage = ''
                    }

                    const canShop = canShopBrand(brand)
                    const hasFileBank =
                      brand.retailer_connections.filter(
                        conn => conn.file_bank === true
                      ).length > 0

                    return (
                      <GridLayoutCompany key={brand.id}>
                        <GridLayoutLogoContainer>
                          {brand.logo && (
                            <GridLayoutLogo
                              brandId={brand.id}
                              id={brand.logo}
                              presets="brand_list_grid_logo"
                              fallback="brand_list_grid_logo"
                              alt={brand.name}
                              title={brand.name}
                            />
                          )}

                          {!brand.logo && <span>{brand.name}</span>}
                        </GridLayoutLogoContainer>
                        <GridLayoutActions>
                          {canShop && (
                            <div>
                              <PermissionContext permission="webshop:browse">
                                <ShopButton
                                  type="button"
                                  onClick={() => onShopBrand(brand)}
                                >
                                  Shop now
                                </ShopButton>
                              </PermissionContext>
                            </div>
                          )}
                          {brand.showrooms.length > 0 && (
                            <div style={{ position: 'relative' }}>
                              <PermissionContext permission="webshop:browse">
                                <ShopButton
                                  type="button"
                                  onClick={() => setShowShowrooms(brand.id)}
                                >
                                  Showrooms
                                </ShopButton>

                                <DropDown
                                  className={
                                    showShowrooms === brand.id ? 'active' : ''
                                  }
                                >
                                  {brand.showrooms.map(showroom => (
                                    <li>
                                      <button
                                        onClick={() =>
                                          onGoToShowroom(brand, showroom)
                                        }
                                      >
                                        {showroom.title}
                                      </button>
                                    </li>
                                  ))}
                                </DropDown>
                              </PermissionContext>
                            </div>
                          )}
                          {!canShop && (
                            <div>
                              <strong>
                                {noAccessMessage.length > 0
                                  ? noAccessMessage
                                  : 'You do not currently have access to this B2B webshop'}
                              </strong>
                            </div>
                          )}
                          <div>
                            <button
                              type="button"
                              className="btn btn-white btn-sm"
                              onClick={() =>
                                onShowOrderImageDownloader(brand.id)
                              }
                            >
                              Download product images
                            </button>
                          </div>
                          {hasFileBank && (
                            <div>
                              <Link
                                to={`/file-bank/${brand.id}`}
                                type="button"
                                className="btn btn-white btn-sm"
                              >
                                Open File bank
                              </Link>
                            </div>
                          )}
                          {retailerConnection &&
                            retailerConnection.dropshipping && (
                              <div>
                                <button
                                  type="button"
                                  className="btn btn-white btn-sm"
                                  onClick={() => onShowDropShippingModal(brand)}
                                >
                                  <span className="label label-xxs label-warning">
                                    NEW
                                  </span>{' '}
                                  Dropshipping
                                </button>
                              </div>
                            )}
                        </GridLayoutActions>
                      </GridLayoutCompany>
                    )
                  })}
                </GridLayoutRow>
              )
            })}
          </GridLayoutContainer>
        </>
      )}
    </>
  )
}

const GridLayoutCompany = styled.div.attrs({ className: 'col-md-4' })`
  align-items: center;
  display: flex;
  flex-direction: column;
`

const GridLayoutContainer = styled.div`
  margin-top: -10px;
`

const GridLayoutRow = styled.div.attrs({ className: 'row' })`
  margin: 50px 0;
`

const GridLayoutLogoContainer = styled.div`
  align-items: center;
  display: flex;
  justify-content: center;
  margin-bottom: 10px;
  max-height: 70px;
  min-height: 70px;
  width: 220px;
`

const GridLayoutActions = styled.div`
  > div {
    margin-top: 15px;
    text-align: center;
  }
`

const GridLayoutLogo = styled(CloudinaryResource)``

const ShopButton = styled.button.attrs({ className: 'btn' })`
  background: #000;
  color: white;

  &:hover,
  &:focus {
    background: #464646;
    color: white;
  }
`

const DropDown = styled.ul`
  position: absolute;
  left: 0;
  top: 0;
  width: 250px;
  border-radius: 8px;
  padding: 10px 0;
  background-color: #fff;
  box-shadow: 0 10px 15px rgba(0, 0, 0, 0.25), 0 -5px 15px rgba(0, 0, 0, 0.1);
  list-style: none;
  text-align: left;
  z-index: 100;

  max-width: 0;
  max-height: 0;
  overflow: auto;
  opacity: 0;

  &.active {
    opacity: 1;
    max-width: 250px;
    max-height: 500px;
  }

  transition: all 0.5s;

  li  {
    padding: 0;

    button {
      color: #000;
      width: 100%;
      font-size: 13px;
      padding: 10px 15px;
      text-transform: uppercase;
      background: transparent;
      text-align: left;
      border: 0;
      display: block;

      &:hover {
        background-color: rgba(100, 100, 100, 0.2);
      }
    }
  }
`

const HeroContainer = styled.div`
  padding: 20px 20px 0 20px;
`

const WelcomeHero = styled.div`
  color: black;
  font-size: 20px;
`

const HeroSubtitle = styled.div`
  color: black;
  font-size: 14px;
`
